/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { exclusion } from './utils';
import { normalizeTextBoxAttachments } from '../../../../utils/textBoxAttachment';

/**
 * Add elements to current page.
 *
 * Elements are expected to a be list of element objects with at least an id property.
 * If any element id already exists on the page, element is skipped (not even updated).
 * If multiple elements in the new list have the same id, only the latter is used.
 *
 * If elements aren't a list or an empty list (after duplicates have been filtered), nothing happens.
 *
 * Elements will be added to the front (end) of the list of elements on the current page,
 * unless `insertAt` is provided to place them at a specific index.
 *
 * Selection is set to be exactly the new elements.
 *
 * @param {Object} state Current state
 * @param {Object} payload Action payload
 * @param {Array.<Object>} payload.elements Elements to insert on the given page.
 * @param {number} payload.insertAt Optional index to insert the elements at.
 * @return {Object} New state
 */
function addElements(state, { elements, insertAt }) {
  if (!Array.isArray(elements)) {
    return state;
  }

  const pageIndex = state.pages.findIndex(({ id }) => id === state.current);
  const oldPage = state.pages[pageIndex];

  const isInitBackground = elements.find((x) => x.isInitBackground === true);

  if (!isInitBackground && oldPage.readonly) return state;

  const newElements = exclusion(oldPage.elements, elements);

  if (newElements.length === 0) {
    return state;
  }

  const newPageElements = [...oldPage.elements];

  if (Number.isFinite(insertAt)) {
    const minIndex = newPageElements.length > 0 ? 1 : 0;
    const maxIndex = newPageElements.length;
    const clampedInsertAt = Math.min(maxIndex, Math.max(minIndex, insertAt));
    newPageElements.splice(clampedInsertAt, 0, ...newElements);
  } else {
    for (const newElement of newElements) {
      let indexToInsert = newPageElements.length;

      if (newElement.groupId) {
        const isExistingGroup = oldPage.groups?.[newElement.groupId];

        // If the element is insert into an existing group, ideally we want to paste it
        // after the element it was copied from. If we can't find it, we paste it at the end of the group.
        // If we're pasting multiple elements, we always add them at the end of the group.
        if (isExistingGroup) {
          const isInsertingMultipleElementInGroup =
            newElements.filter((el) => el.groupId === newElement.groupId)
              .length > 1;
          const indexOfCopiedElement = isInsertingMultipleElementInGroup
            ? -1
            : newPageElements.findIndex(
                (el) =>
                  el.id === newElement.basedOn &&
                  el.groupId === newElement.groupId
              );

          if (indexOfCopiedElement !== -1) {
            indexToInsert = indexOfCopiedElement + 1;
          } else {
            const indexOfLastElementInGroup = newPageElements.findLastIndex(
              (el) => el.groupId === newElement.groupId
            );

            if (indexOfLastElementInGroup !== -1) {
              indexToInsert = indexOfLastElementInGroup + 1;
            }
          }
        }
      }

      if (indexToInsert >= newPageElements.length) {
        newPageElements.push(newElement);
      } else {
        newPageElements.splice(indexToInsert, 0, newElement);
      }
    }
  }

  const newPage = {
    ...oldPage,
    elements: normalizeTextBoxAttachments(newPageElements),
  };

  const newPages = [
    ...state.pages.slice(0, pageIndex),
    newPage,
    ...state.pages.slice(pageIndex + 1),
  ];

  const newSelection = newElements.map(({ id }) => id);

  return {
    ...state,
    pages: newPages,
    selection: newSelection,
  };
}

export default addElements;
